import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useMemo } from "react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { getUserRights } from "../../../../../core/router/auth/SecurityManager";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { useTerminalProfileLoadQuery, useTerminalProfileUpdateMutation } from "../../services/server/TerminalProfileApi";
import type { TerminalProfileEntity } from "../../types/TerminalProfileEntity";

export const TerminalProfileEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.TERMINAL_PROFILES}`);
    const { id } = useParams();

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("terminal-profile");

    const [formData, setFormData] = useState<TerminalProfileEntity>({} as TerminalProfileEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useTerminalProfileLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);

    // check user rights
    const hasAppVersionEditPermission = useMemo(() => {
        const rights = getUserRights();
        return rights?.includes("TERMINAL_PROFILE_EDIT_APPLICATION_VERSION");
    }, []);

    // modify applicationVersion field to be read-only if user doesn't have permission
    const processedFormDefinition = useMemo(() => {
        if (!formDefinition?.formConfiguration) return null;

        const hasPermission = hasAppVersionEditPermission;

        return {
            ...formDefinition.formConfiguration,
            tabs: formDefinition.formConfiguration.tabs.map((tab) => ({
                ...tab,
                fields: tab.fields.map((field) =>
                    field.name === "applicationVersion"
                        ? { ...field, attributes: { ...field.attributes, readOnly: !hasPermission } }
                        : field,
                ),
            })),
        };
    }, [formDefinition, hasAppVersionEditPermission]);

    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useTerminalProfileUpdateMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(i18n.t("common.formUpdateSuccess", { id: result.data.id, resource: "TerminalProfile" }));
            }
        },
        [saveFormData, formData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {processedFormDefinition && (
                <FormManager<TerminalProfileEntity>
                    name="TerminalProfileEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={processedFormDefinition}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
